import React, { useEffect, useMemo, useRef, useState } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { useRovoEntitlement } from '@atlassian/conversation-assistant-entitlement/controllers/rovo-entitlement';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { usePageContextType } from '@atlassian/rovo-conversation-actions/rovo-conversation-starters';
import { PageContextType } from '@atlassian/rovo-conversation-actions/types';

import { isBrowser } from '../../common/utils/is-browser';

const ELIGIBLE_PAGES = [
	PageContextType.JIRA_FOR_YOU,
	PageContextType.JIRA_ISSUE_VIEW,
	PageContextType.JIRA_PROJECT,
];
const ELIGIBLE_PRODUCTS = ['jira'];
const DWELL_TIME_MS = 5000;

export const OnboardingEnablement = ({ product }: { product: string }) => {
	if (isBrowser() && fg('rovo_onboarding_modal_jira_chat_fg')) {
		return <OnboardingEnablementInner product={product} />;
	}

	return null;
};

const OnboardingEnablementInner = ({ product }: { product: string }) => {
	useOnboardingEnablement({ product });
	return null;
};

function useOnboardingEnablement({ product }: { product: string }): void {
	const pageContextType = usePageContextType();
	const { isRovoEnabled } = useRovoEntitlement();
	const { mounted: isChatOpen, setIsChatOnboardingMiniModalOpen } = useUIStore();
	const publish = usePublish('ai-mate');

	// TODO: this will evolve to the holdout (likely local storage)
	const hasPublishedOnboardingRef = useRef(false);
	const hasOpenedChatRef = useRef(false);
	const routeDwellTimerRef = useRef<NodeJS.Timeout | null>(null);
	const [hasDwelled, setHasDwelled] = useState(false);

	const isFeatureGateEnabled = fg('rovo_onboarding_modal_jira_chat_fg');
	const isEligiblePage = ELIGIBLE_PAGES.includes(pageContextType);
	const isEligibleProduct = ELIGIBLE_PRODUCTS.includes(product);

	// Track if chat has been opened
	useEffect(() => {
		if (isChatOpen && !hasOpenedChatRef.current) {
			hasOpenedChatRef.current = true;
		}
	}, [isChatOpen]);

	// Dwell timer effect - only runs when basic eligibility is met
	useEffect(() => {
		const shouldSkipTimer =
			hasOpenedChatRef.current ||
			hasPublishedOnboardingRef.current ||
			!isFeatureGateEnabled ||
			!isRovoEnabled ||
			!isEligiblePage ||
			!isEligibleProduct;

		if (shouldSkipTimer) {
			return;
		}

		// Reset dwell status when route changes
		setHasDwelled(false);

		// Clear any existing timer
		if (routeDwellTimerRef.current) {
			clearTimeout(routeDwellTimerRef.current);
			routeDwellTimerRef.current = null;
		}

		// Start new timer for current route
		routeDwellTimerRef.current = setTimeout(() => {
			setHasDwelled(true);
			routeDwellTimerRef.current = null;
		}, DWELL_TIME_MS);

		// Cleanup on unmount or route change
		return () => {
			if (routeDwellTimerRef.current) {
				clearTimeout(routeDwellTimerRef.current);
				routeDwellTimerRef.current = null;
			}
		};
	}, [pageContextType, isRovoEnabled, isFeatureGateEnabled, isEligiblePage, isEligibleProduct]);

	const isEligible = useMemo(
		() =>
			isRovoEnabled &&
			isFeatureGateEnabled &&
			isEligiblePage &&
			isEligibleProduct &&
			hasDwelled &&
			!isChatOpen,
		[
			isRovoEnabled,
			isFeatureGateEnabled,
			isEligiblePage,
			isEligibleProduct,
			hasDwelled,
			isChatOpen,
		],
	);

	useEffect(() => {
		if (isEligible && !hasPublishedOnboardingRef.current && !hasOpenedChatRef.current) {
			const cohort = FeatureGates.getExperimentValue<string>(
				'rovo_onboarding_modal_jira_chat',
				'cohort',
				'',
			);

			if (cohort === 'variation') {
				hasPublishedOnboardingRef.current = true;
				hasOpenedChatRef.current = true;
				setIsChatOnboardingMiniModalOpen(true);

				publish({
					type: 'chat-draft',
					product,
					source: 'ai-mate-onboarding',
					openChat: true,
					openChatMode: 'mini-modal',
				});
			}
		}
	}, [isEligible, product, setIsChatOnboardingMiniModalOpen, publish]);
}
